import React from "react";
import { decodeHtml } from "@/lib/utils";
import { sanitizeHTML, normalizeContent } from "@/lib/utils/textFormatting";
import { cn } from "@/lib/utils";

interface StyledTextDisplayProps {
  content: string;
  className?: string;
  /** Maximum number of lines to display before truncating (uses line-clamp) */
  maxLines?: number;
}

/**
 * A reusable component for displaying styled/HTML text content in read-only mode.
 * Automatically handles HTML decoding, sanitization, and proper rendering.
 * 
 * @example
 * ```tsx
 * <StyledTextDisplay 
 *   content={description} 
 *   maxLines={6}
 *   className="text-sm"
 * />
 * ```
 */
export const StyledTextDisplay: React.FC<StyledTextDisplayProps> = ({
  content,
  className = "",
  maxLines,
}) => {
  // Helper function to check if text contains styling elements
  const hasStylingElements = (text: string): boolean => {
    if (!text || typeof text !== 'string') return false;
    
    // Check for HTML tags
    const htmlTagRegex = /<[^>]*>/;
    if (htmlTagRegex.test(text)) return true;
    
    // Check for markdown elements
    const markdownRegex = /[*_`~#[\]()<>|]/;
    if (markdownRegex.test(text)) return true;
    
    // Check for common formatting patterns
    const formattingRegex = /(\*\*|__|\*\*|__|`{1,3}|~~|#{1,6}|\[.*?\]|\(.*?\)|>|\|)/;
    if (formattingRegex.test(text)) return true;
    
    // Check for URLs
    const urlRegex = /https?:\/\/[^\s]+/;
    if (urlRegex.test(text)) return true;
    
    return false;
  };

  // Process text for display
  const processedContent = React.useMemo(() => {
    if (!content || typeof content !== 'string') return content;
    
    // If text contains HTML, process it safely
    if (hasStylingElements(content)) {
      try {
        // Decode HTML entities first
        const decodedText = decodeHtml(content);
        // Sanitize and normalize the HTML
        const sanitizedText = sanitizeHTML(decodedText);
        const normalizedText = normalizeContent(sanitizedText);
        
        return normalizedText;
      } catch (error) {
        console.error('Error processing HTML text:', error);
        // Fallback to plain text if processing fails
        return content;
      }
    }
    
    // Return plain text as-is
    return content;
  }, [content]);

  // Build className with optional line-clamp
  const displayClassName = cn(
    "leading-relaxed",
    maxLines && `line-clamp-${maxLines}`,
    className
  );

  // If content has styling, render with dangerouslySetInnerHTML
  if (typeof processedContent === 'string' && hasStylingElements(content)) {
    return (
      <div 
        className={displayClassName}
        dangerouslySetInnerHTML={{ __html: processedContent }}
      />
    );
  }

  // Otherwise render as plain text
  return (
    <div className={displayClassName}>
      {processedContent}
    </div>
  );
};

export default StyledTextDisplay;


